import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { timeout, catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'src/app/services/shared/local-storage/local-storage.service';
import { GenerateTokenService, Token } from 'integrationpingid-lib';

// ────────────────────────────────────────────────────────────────────────────
// Interfaces de respuesta
// ────────────────────────────────────────────────────────────────────────────

/** Detalle individual de la respuesta DIS */
export interface DisResponseDetail {
  dataIdentifier: string;
  stage: string;
  dataDetails: Record<string, string>;
}

/** Respuesta estándar del servicio DIS / DIS Global / Fields / Dispute */
export interface DisResponse {
  status: string;
  message: string;
  requestId: string;
  dfCorrelationId: string;
  responseTime: number;
  details?: DisResponseDetail[];
  additionalInfo?: Record<string, string>;
}

/**
 * Payload para DIS v2 (estructura completa con data[] en string).
 * Opcionalmente acepta purposingProfiles (Delta) para ingestar con filtros de exposición.
 */
export interface DisPayload {
  data: string[];
  audit?: Record<string, any>;
  purposingProfiles?: string[];
}

/**
 * Payload para DIS Global v1.
 * Se compone de campos de matchkey + newValues + (opcional) purposingProfiles + (opcional) newIngest + (opcional) audit
 */
export interface DisGlobalPayload {
  entityKey?: string;
  iup?: string;
  matchKey?: string;
  [matchKeyField: string]: any;
  newValues?: Record<string, any>;
  jour_filter?: Record<string, any>;
  purposingProfiles?: string[];
  newIngest?: {
    request: Record<string, any>;
  };
  audit?: Record<string, any>;
}

/** Payload para Dispute */
export interface DisputePayload {
  entityKey: string;
  iup: string;
  matrizCod: string;
  comentario?: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Servicio
// ────────────────────────────────────────────────────────────────────────────

/**
 * Servicio centralizado para consumir los endpoints del microservicio DIS
 * (Data Ingestion Service) documentados en la API de backend.
 *
 * Endpoints soportados:
 *  - DIS v2        POST /api/v2/DIS/{country}/{domain}/{subdomain}
 *  - DIS Global v1 POST /api/v1/global/{country}/{domain}/{subdomain}
 *  - Dataview v2   GET  /api/v2/schema/{country}/{domain}/{subdomain}
 *  - Matchkey v1   GET  /api/v1/matchkey/{country}/{domain}/{subdomain}
 *  - Fields v2     POST /api/v2/fields/{country}/{domain}/{subdomain}  (facade-apigee)
 *  - Dispute v1    POST /api/v1/dispute/credit/{country}/{domain}/{subdomain}
 *
 * IMPORTANTE: Este servicio NO reemplaza los métodos DIS existentes en
 * SharedService, FuenteDinamicaService, BcraService, TradeService ni EcDisService.
 * Convive en paralelo para poder migrar progresivamente y revertir rápidamente.
 */
@Injectable({
  providedIn: 'root'
})
export class DisService {

  // ── Constantes de rutas ────────────────────────────────────────────────
  private readonly VIP_PATH = '/vip';
  private readonly API_PATH = '/api';
  private readonly DIS_SERVICE_NAME = 'dis-service';

  /** Facade por defecto (DIS, DIS Global, Dataview, Matchkey, Dispute) */
  private readonly FACADE_IM = 'facade-im';
  /** Facade para el servicio Fields (expuesto solo por facade-apigee) */
  private readonly FACADE_APIGEE = 'facade-apigee';

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private serviceToken: GenerateTokenService
  ) { }

  // ── Helpers de autenticación ───────────────────────────────────────────

  /** Indica si el usuario se autenticó por VIP (Oidc) */
  private get isAuthVip(): boolean {
    return this.localStorageService.getIsVip;
  }

  /** Genera los headers de autorización según el tipo de autenticación */
  private getTokenToHeaders(): { Authorization: string } {
    if (this.isAuthVip) {
      return { Authorization: 'Bearer ' + localStorage.getItem('access_token') };
    }
    let token: Token = {};
    if (this.serviceToken.statusTokenUser()) {
      token = this.serviceToken.getTokenUser();
    }
    return { Authorization: token.token_type + ' ' + token.access_token };
  }

  private get httpOptions() {
    return { headers: this.getTokenToHeaders() };
  }

  // ── URL Builders ───────────────────────────────────────────────────────

  /**
   * Construye la URL base para los endpoints de dis-service (facade-im).
   *
   * VIP  (Oidc):   {F_VIP_URL_BASE_API}/vip/dis-service/{version}/{endpointPath}
   * DM   (PingID): {F_PINGID_URL_BASE_API}/facade-im/dis-service/api/{version}/{endpointPath}
   *
   * @param version  Versión de la API (ej: 'v1', 'v2')
   * @param endpointPath Ruta del endpoint (ej: 'DIS', 'global', 'schema', 'matchkey', 'dispute/credit')
   * @param country  Código de país
   * @param domain   Dominio
   * @param subdomain Subdominio
   */
  private buildUrl(
    version: string,
    endpointPath: string,
    country: string,
    domain: string,
    subdomain: string
  ): string {
    const urlBaseApi = this.isAuthVip
      ? this.localStorageService.configInit['F_VIP_URL_BASE_API']
      : this.localStorageService.configInit['F_PINGID_URL_BASE_API'];

    if (this.isAuthVip) {
      // VIP: {base}/vip/dis-service/{version}/{endpoint}/{country}/{domain}/{subdomain}
      return `${urlBaseApi}${this.VIP_PATH}/${this.DIS_SERVICE_NAME}/${version}/${endpointPath}/${country}/${domain}/${subdomain}`;
    } else {
      // DM:  {base}/facade-im/dis-service/api/{version}/{endpoint}/{country}/{domain}/{subdomain}
      const facadePath = (environment.MICROSERVICIO.toString().toLowerCase() === 'true')
        ? `/${this.FACADE_IM}/${this.DIS_SERVICE_NAME}`
        : '';
      return `${urlBaseApi}${facadePath}${this.API_PATH}/${version}/${endpointPath}/${country}/${domain}/${subdomain}`;
    }
  }

  /**
   * Construye la URL para el endpoint Fields que usa facade-apigee.
   * Fields solo se expone a través de facade-apigee (no tiene ruta VIP conocida).
   *
   * DM: {F_PINGID_URL_BASE_API}/facade-apigee/dis-service/api/{version}/fields/{country}/{domain}/{subdomain}
   */
  private buildFieldsUrl(
    version: string,
    country: string,
    domain: string,
    subdomain: string
  ): string {
    const urlBaseApi = this.isAuthVip
      ? this.localStorageService.configInit['F_VIP_URL_BASE_API']
      : this.localStorageService.configInit['F_PINGID_URL_BASE_API'];

    if (this.isAuthVip) {
      // No hay documentación de ruta VIP para Fields; se usa la misma convención
      return `${urlBaseApi}${this.VIP_PATH}/${this.DIS_SERVICE_NAME}/${version}/fields/${country}/${domain}/${subdomain}`;
    } else {
      const facadePath = (environment.MICROSERVICIO.toString().toLowerCase() === 'true')
        ? `/${this.FACADE_APIGEE}/${this.DIS_SERVICE_NAME}`
        : '';
      return `${urlBaseApi}${facadePath}${this.API_PATH}/${version}/fields/${country}/${domain}/${subdomain}`;
    }
  }

  // ── Validador de consumo de API ────────────────────────────────────────

  /** Indica si está habilitado el consumo real de datos */
  private get isApiEnabled(): boolean {
    return environment.CONSUME_API_DATA.toString().toLowerCase() === 'true';
  }

  // ────────────────────────────────────────────────────────────────────────
  // ENDPOINTS PÚBLICOS
  // ────────────────────────────────────────────────────────────────────────

  /**
   * **DIS v2** — Ingesta una observación completa (estructura armada por el front).
   *
   * POST /api/v2/DIS/{country}/{domain}/{subdomain}
   *
   * @param country   Código de país (ej: 'AR')
   * @param domain    Dominio (ej: 'AR_IDENTITY')
   * @param subdomain Subdominio (ej: 'NATURAL_PERSON')
   * @param payload   Objeto con data[] (string JSON de la observación)
   */
  postDis(country: string, domain: string, subdomain: string, payload: DisPayload): Observable<DisResponse> {
    const url = this.buildUrl('v2', 'DIS', country.toUpperCase(), domain, subdomain);
    console.log('[DisService] postDis URL:', url);

    if (!this.isApiEnabled) {
      console.log('[DisService] postDis MOCK — API deshabilitada');
      return of({ status: 'mock', message: 'Mock response', requestId: '', dfCorrelationId: '', responseTime: 0 });
    }

    return this.http.post<DisResponse>(url, payload, this.httpOptions).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      map(response => {
        // APIGEE devuelve 200 con body vacío cuando el token es inválido o el flujo falla en VIP
        if (this.isAuthVip && (response === null || response === undefined || (typeof response === 'object' && Object.keys(response).length === 0))) {
          throw { status: 200, message: 'Respuesta vacía del servidor (VIP DIS)', vipEmptyResponse: true };
        }
        return response;
      }),
      catchError(error => {
        console.error('[DisService] postDis error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * **DIS Global v1** — Ingesta una observación donde el backend arma la estructura contra DIS.
   * Requiere los campos de matchkey + newValues.
   * Opcionalmente acepta purposingProfiles (Delta) y newIngest (entidades nuevas).
   *
   * POST /api/v1/global/{country}/{domain}/{subdomain}
   *
   * @param country   Código de país
   * @param domain    Dominio
   * @param subdomain Subdominio
   * @param payload   Objeto con matchkey fields, newValues, etc.
   */
  postDisGlobal(country: string, domain: string, subdomain: string, payload: DisGlobalPayload): Observable<DisResponse> {
    const url = this.buildUrl('v1', 'global', country.toUpperCase(), domain, subdomain);
    console.log('[DisService] postDisGlobal URL:', url);

    if (!this.isApiEnabled) {
      console.log('[DisService] postDisGlobal MOCK — API deshabilitada');
      return of({ status: 'mock', message: 'Mock response', requestId: '', dfCorrelationId: '', responseTime: 0 });
    }

    return this.http.post<DisResponse>(url, payload, this.httpOptions).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      map(response => {
        if (this.isAuthVip && (response === null || response === undefined || (typeof response === 'object' && Object.keys(response).length === 0))) {
          throw { status: 200, message: 'Respuesta vacía del servidor (VIP DIS)', vipEmptyResponse: true };
        }
        return response;
      }),
      catchError(error => {
        console.error('[DisService] postDisGlobal error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * **Dataview v2** — Consulta del schema STD de ingesta.
   * Retorna los campos del objeto "data" dentro de la observación.
   *
   * GET /api/v2/schema/{country}/{domain}/{subdomain}
   *
   * @param country   Código de país
   * @param domain    Dominio
   * @param subdomain Subdominio
   */
  getDataview(country: string, domain: string, subdomain: string): Observable<any> {
    const url = this.buildUrl('v2', 'schema', country.toUpperCase(), domain, subdomain);
    console.log('[DisService] getDataview URL:', url);

    if (environment.CONSUME_API_SCHEMA.toString().toLowerCase() !== 'true') {
      // Fallback a mock local
      const mockUrl = `assets/mock/${country.toLowerCase()}/dataview_schema/DATAVIEW_${domain}_${subdomain}.json`;
      console.log('[DisService] getDataview MOCK URL:', mockUrl);
      return this.http.get<any>(mockUrl).pipe(
        timeout(Number(environment.HTTP_REQUEST_TIMEOUT))
      );
    }

    return this.http.get<any>(url, this.httpOptions).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      catchError(error => {
        console.error('[DisService] getDataview error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * **Matchkey v1** — Consulta los campos que conforman la matchkey de una fuente.
   *
   * GET /api/v1/matchkey/{country}/{domain}/{subdomain}
   *
   * @param country   Código de país
   * @param domain    Dominio
   * @param subdomain Subdominio
   * @returns Observable con un array de strings (nombres de campos de la matchkey)
   */
  getMatchkey(country: string, domain: string, subdomain: string): Observable<string[]> {
    const url = this.buildUrl('v1', 'matchkey', country.toUpperCase(), domain, subdomain);
    console.log('[DisService] getMatchkey URL:', url);

    if (!this.isApiEnabled) {
      console.log('[DisService] getMatchkey MOCK — API deshabilitada');
      return of([]);
    }

    return this.http.get<string[]>(url, this.httpOptions).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      catchError(error => {
        console.error('[DisService] getMatchkey error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * **Fields v2** — Ingesta un campo específico sobre una entidad.
   * Solo para TRADE (RD) y DELINQUENCY_BED_EXCH (CL).
   * Expuesto a través de facade-apigee (no facade-im).
   *
   * POST /api/v2/fields/{country}/{domain}/{subdomain}
   *
   * @param country   Código de país (ej: 'RD', 'CL')
   * @param domain    Dominio (ej: 'DO_CREDIT')
   * @param subdomain Subdominio (ej: 'TRADE')
   * @param payload   Datos a enviar
   */
  postFields(country: string, domain: string, subdomain: string, payload: any): Observable<DisResponse> {
    const url = this.buildFieldsUrl('v2', country.toUpperCase(), domain, subdomain);
    console.log('[DisService] postFields URL:', url);

    if (!this.isApiEnabled) {
      console.log('[DisService] postFields MOCK — API deshabilitada');
      return of({ status: 'mock', message: 'Mock response', requestId: '', dfCorrelationId: '', responseTime: 0 });
    }

    return this.http.post<DisResponse>(url, payload, this.httpOptions).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      catchError(error => {
        console.error('[DisService] postFields error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * **Dispute v1** — Da de baja Tarjetas de Crédito en BCRA.
   * Obtiene los últimos 24 periodos con situacion_cod > 1 e ingesta modificaciones.
   *
   * POST /api/v1/dispute/credit/{country}/{domain}/{subdomain}
   *
   * @param country   Código de país (ej: 'AR')
   * @param domain    Dominio (ej: 'AR_BUREAU_EXCH')
   * @param subdomain Subdominio (ej: 'CREDIT_CARD')
   * @param payload   Datos del dispute (entityKey, iup, matrizCod, comentario)
   */
  postDispute(country: string, domain: string, subdomain: string, payload: DisputePayload): Observable<DisResponse> {
    const url = this.buildUrl('v1', 'dispute/credit', country.toUpperCase(), domain, subdomain);
    console.log('[DisService] postDispute URL:', url);

    if (!this.isApiEnabled) {
      console.log('[DisService] postDispute MOCK — API deshabilitada');
      return of({ status: 'mock', message: 'Mock response', requestId: '', dfCorrelationId: '', responseTime: 0 });
    }

    return this.http.post<DisResponse>(url, payload, this.httpOptions).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      catchError(error => {
        console.error('[DisService] postDispute error:', error);
        return throwError(() => error);
      })
    );
  }
}
